<script setup lang="ts">
/** One agent run: goal, live transcript (step by step), summary, findings, proposed actions, and for code agents the branch and pull request; cancel while it runs. */
import { Ban, CircleAlert, Code, ExternalLink, GitBranch, GitPullRequest, MessageSquare, Lightbulb, Wrench } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, nextTick, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";
import { cancelAgentRun, isActive, useAgentRun, useAgents, type RunEvent } from "@/api/agents";
import { codeResult, prLabel, runTitle, type AgentsCatalog } from "@/components/agents/agentMeta";
import CopyButton from "@/components/common/CopyButton.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import JsonView from "@/components/common/JsonView.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import MarkdownView from "@/components/common/MarkdownView.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import StatusBadge from "@/components/common/StatusBadge.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { confirm } from "@/composables/useConfirm";
import { errorMessage } from "@/lib/errors";
import { formatDuration, formatUsd } from "@/lib/format";

definePage({ meta: { title: "Agent run", requires: { capability: "agents.read" } } });

const route = useRoute("/agents/runs/[id]");
const queryClient = useQueryClient();
const run = useAgentRun(() => route.params.id);
const data = computed(() => run.data.value);
const active = computed(() => (data.value ? isActive(data.value) : false));
const duration = computed(() => (data.value?.startedAt ? new Date(data.value.finishedAt ?? Date.now()).getTime() - new Date(data.value.startedAt).getTime() : null));
const proposals = computed(() => data.value?.report?.proposed_actions ?? []);
const findings = computed(() => data.value?.report?.findings ?? []);
const code = computed(() => (data.value ? codeResult(data.value) : null));
const agents = useAgents();
const title = computed(() => (data.value ? runTitle(data.value, agents.data.value as AgentsCatalog | undefined) : ""));
const codeNote = computed(() => {
  const c = code.value;
  if (!c) return "";
  if (c.pr) return "Review the draft pull request — agents never merge.";
  if (active.value) return "Working on its own branch in the codespace.";
  if (c.commits === null) return "No pull request.";
  return c.commits ? "No pull request: the commits are on the branch in the codespace." : "Nothing was committed.";
});

const ICON = { assistant: MessageSquare, tool_call: Wrench, tool_result: Wrench, proposal: Lightbulb, error: CircleAlert } as const;
const icon = (e: RunEvent) => ICON[e.kind as keyof typeof ICON] ?? MessageSquare;
const label = (e: RunEvent) => (e.kind === "tool_call" ? `calls ${e.tool}` : e.kind === "tool_result" ? `${e.tool} answered` : e.kind === "proposal" ? `proposes ${e.tool}` : e.kind);

// Follow the transcript while it runs.
const bottom = ref<HTMLElement | null>(null);
watch(() => data.value?.events.length, async (n, prev) => {
  if (!active.value || !n || n === prev) return;
  await nextTick();
  bottom.value?.scrollIntoView({ block: "nearest", behavior: "smooth" });
});

async function cancel() {
  if (!data.value || !(await confirm({ title: "Stop this run?", description: "It keeps what it found so far.", confirmLabel: "Stop", destructive: true }))) return;
  try {
    await cancelAgentRun(data.value.id);
    await queryClient.invalidateQueries({ queryKey: ["agents"] });
    toast.success("Stopping…");
  } catch (err) {
    toast.error(errorMessage(err));
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <ListSkeleton v-if="run.isPending.value" :rows="6" />
    <ErrorState v-else-if="run.error.value" :error="run.error.value" @retry="run.refetch()" />
    <template v-else-if="data">
      <PageHeader :title="title" :description="data.goal" :crumbs="[{ label: 'Agents', to: '/agents' }, { label: 'Runs', to: '/agents/runs' }, { label: title }]">
        <template #badges><StatusBadge :status="data.status" /></template>
        <template #meta>
          <span class="text-xs text-muted-foreground">
            {{ data.trigger }} · as {{ data.username ?? "—" }} · <RelativeTime :value="data.createdAt" />
            <template v-if="duration !== null"> · {{ formatDuration(duration) }}</template>
            · {{ data.toolCalls }} tool calls · {{ formatUsd(data.costUsd) }}
            <template v-if="data.stopReason && data.stopReason !== 'completed'"> · stopped: {{ data.stopReason }}</template>
          </span>
        </template>
        <template #actions>
          <RouterLink v-if="data.todoId" :to="`/todos?highlight=${data.todoId}`" class="text-sm underline-offset-2 hover:underline">Todo</RouterLink>
          <RouterLink v-if="data.jobId" :to="`/jobs/${data.jobId}`" class="text-sm underline-offset-2 hover:underline">Job</RouterLink>
          <Button v-if="active" variant="outline" @click="cancel"><Ban /> Stop</Button>
        </template>
      </PageHeader>

      <p v-if="data.error" class="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">{{ data.error }}</p>

      <SectionCard v-if="code" title="Code" :icon="Code" :description="codeNote">
        <dl class="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-[auto_1fr]">
          <template v-if="code.repo"><dt class="text-muted-foreground">Repository</dt><dd><a :href="`https://github.com/${code.repo}`" target="_blank" rel="noopener" class="underline-offset-2 hover:underline">{{ code.repo }}</a></dd></template>
          <template v-if="code.codespace"><dt class="text-muted-foreground">Codespace</dt><dd class="font-mono text-xs">{{ code.codespace }}</dd></template>
          <template v-if="code.branch">
            <dt class="text-muted-foreground">Branch</dt>
            <dd class="flex min-w-0 items-center gap-1"><GitBranch class="size-3.5 shrink-0 text-muted-foreground" /><span class="truncate font-mono text-xs">{{ code.branch }}</span><CopyButton :value="code.branch" label="Copy branch" class="-my-2" /></dd>
          </template>
          <template v-if="code.commits !== null"><dt class="text-muted-foreground">Commits</dt><dd>{{ code.commits }}<template v-if="code.changes"> · {{ code.changes }} uncommitted change{{ code.changes === 1 ? "" : "s" }}</template></dd></template>
          <template v-if="code.pr">
            <dt class="text-muted-foreground">Pull request</dt>
            <dd><a :href="code.pr" target="_blank" rel="noopener" class="inline-flex items-center gap-1.5 font-medium text-success underline-offset-2 hover:underline"><GitPullRequest class="size-3.5" /> {{ prLabel(code.pr) }} <ExternalLink class="size-3" /></a></dd>
          </template>
        </dl>
      </SectionCard>

      <SectionCard v-if="data.summaryMd" title="Result">
        <MarkdownView :source="data.summaryMd" />
        <ul v-if="findings.length" class="mt-4 flex flex-col gap-2">
          <li v-for="(f, i) in findings" :key="i" class="rounded-md border p-2 text-sm">
            <span class="font-medium">{{ f.title }}</span>
            <span class="block text-xs text-muted-foreground">{{ f.evidence }}</span>
            <a v-if="f.uri && /^https?:/.test(f.uri)" :href="f.uri" target="_blank" rel="noopener" class="text-xs underline">{{ f.uri }}</a>
            <span v-else-if="f.uri" class="font-mono text-xs">{{ f.uri }}</span>
          </li>
        </ul>
      </SectionCard>

      <SectionCard v-if="proposals.length" title="Proposed actions" :icon="Lightbulb" description="Nothing was changed. Review these and run them yourself (in Claude or the tools playground).">
        <ul class="flex flex-col gap-3">
          <li v-for="(p, i) in proposals" :key="i" class="rounded-md border p-3">
            <div class="flex items-center gap-2"><Badge variant="outline" class="font-mono font-normal">{{ p.tool }}</Badge><span class="text-sm">{{ p.reason }}</span></div>
            <JsonView :value="p.arguments" class="mt-2" />
          </li>
        </ul>
      </SectionCard>

      <SectionCard :title="active ? 'Live transcript' : 'Transcript'" :icon="MessageSquare">
        <p v-if="!data.events.length" class="text-sm text-muted-foreground">{{ active ? "Waiting for the first step…" : "No steps were recorded." }}</p>
        <ol v-else class="flex flex-col gap-2">
          <li v-for="e in data.events" :key="e.id" :class="['rounded-md border p-2 text-sm', e.isError ? 'border-destructive/40' : '', e.kind === 'proposal' ? 'border-warning/40 bg-warning/5' : '']">
            <div class="flex items-center gap-2 text-xs text-muted-foreground">
              <component :is="icon(e)" class="size-3.5" />
              <span>turn {{ e.turn }} · {{ label(e) }}</span>
              <RelativeTime :value="e.at" class="ml-auto" />
            </div>
            <MarkdownView v-if="e.kind === 'assistant'" :source="e.text" class="mt-1" />
            <pre v-else class="mt-1 max-h-60 overflow-auto font-mono text-xs whitespace-pre-wrap">{{ e.text }}</pre>
          </li>
        </ol>
        <div ref="bottom" />
      </SectionCard>
    </template>
  </div>
</template>
